import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { evacuationPlanService } from '../../services/carehome_manager';
import {
  Modal, Select, Button, PageHeader, DataTable, Avatar, StatusPill,
} from '../../components';
import { toast } from 'react-hot-toast';
import {
  Building2, Users, CheckCircle2, AlertTriangle, Plus, Map, Pencil, Upload,
  Trash2, FileText, UserPlus,
} from 'lucide-react';

/**
 * Evacuation Plan — name evacuation zones, keep floor-plan maps current, and
 * record each resident's evacuation detail (room, support level, method, plan).
 * The staff app reads exactly this, read-only, so anything saved here is what
 * carers see in an emergency.
 *
 * Backed by /api/carehome-manager/evacuation-plan. Every write returns the whole
 * refreshed plan, so state is replaced from the response rather than refetched.
 */

const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const MAX_MAP_BYTES = 2 * 1024 * 1024;

// Support level → pill tone + dot colour. Shared with the app's legend.
const SUPPORT_META = {
  full: { tone: 'crit', label: 'Full assistance', dot: '#E11D48' },
  assisted: { tone: 'warn', label: 'Assisted', dot: '#D97706' },
  independent: { tone: 'good', label: 'Independent', dot: '#059669' },
};

const SUPPORT_OPTIONS = [
  { value: 'full', label: 'Full assistance' },
  { value: 'assisted', label: 'Assisted' },
  { value: 'independent', label: 'Independent' },
];

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Metric = ({ label, value, hint, icon: Icon, tone }) => (
  <div className="bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[11px] font-bold tracking-wider text-[#94A3B8] dark:text-gray-500 uppercase">{label}</div>
        <div className="text-[34px] leading-none font-extrabold text-[#0B1220] dark:text-gray-100 mt-3 tabular-nums">{value}</div>
        <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2">{hint}</div>
      </div>
      <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${tone}`}>
        <Icon className="w-5 h-5" strokeWidth={1.9} />
      </span>
    </div>
  </div>
);

const SupportDot = ({ level }) => (
  <span
    className="w-2 h-2 rounded-full inline-block shrink-0"
    style={{ background: SUPPORT_META[level]?.dot || '#94A3B8' }}
  />
);

const EvacuationPlan = () => {
  const [zones, setZones] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Zone editor (create + manage share one modal, as in the flow)
  const [zoneModal, setZoneModal] = useState(null); // null | {} | zone
  const [zoneName, setZoneName] = useState('');
  const [zoneMapFile, setZoneMapFile] = useState(null);

  // Zone residents drawer, then the two resident modals it opens
  const [residentsZone, setResidentsZone] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addResidenceId, setAddResidenceId] = useState('');
  const [editResident, setEditResident] = useState(null);
  const [form, setForm] = useState({ zone_id: '', room: '', support_level: 'assisted', method: '', plan: '' });

  // Map viewer
  const [mapZone, setMapZone] = useState(null);

  const applyPlan = (res) => {
    setZones(res.zones || []);
    setUnassigned(res.unassignedResidents || []);
    setStats(res.stats || {});
    // Keep the open residents drawer pointing at fresh data.
    setResidentsZone((prev) => (prev ? (res.zones || []).find((z) => z.id === prev.id) || null : null));
  };

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await evacuationPlanService.getPlan();
      if (res.success) applyPlan(res);
      else toast.error(res.message || 'Failed to load evacuation plan');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load evacuation plan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  // Every mutation funnels through here — one place for the toast + state swap.
  const run = async (fn, fallbackError) => {
    setSaving(true);
    try {
      const res = await fn();
      if (res.success) {
        applyPlan(res);
        if (res.message) toast.success(res.message);
        return true;
      }
      toast.error(res.message || fallbackError);
      return false;
    } catch (err) {
      toast.error(err.response?.data?.message || fallbackError);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ── Zone editor ───────────────────────────────────────────────────────────
  const openZoneEditor = (zone = null) => {
    setZoneModal(zone || {});
    setZoneName(zone?.name || '');
    setZoneMapFile(null);
  };

  const pickMap = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MAP_BYTES) {
      toast.error('Choose a map file smaller than 2 MB');
      e.target.value = '';
      return;
    }
    setZoneMapFile(file);
  };

  const submitZone = async () => {
    const name = zoneName.trim();
    if (!name) {
      toast.error('Enter a zone name');
      return;
    }
    const isEdit = !!zoneModal?.id;
    const ok = await run(
      () => (isEdit
        ? evacuationPlanService.updateZone(zoneModal.id, { name, map: zoneMapFile })
        : evacuationPlanService.createZone({ name, map: zoneMapFile })),
      isEdit ? 'Failed to update zone' : 'Failed to create zone'
    );
    if (ok) setZoneModal(null);
  };

  const removeZone = async (zone) => {
    // Deleting a zone never destroys plan text — residents just become
    // unassigned — but it's still worth confirming.
    const msg = zone.residentCount > 0
      ? `Delete ${zone.name}? Its ${zone.residentCount} resident(s) will become unassigned. Their evacuation details are kept.`
      : `Delete ${zone.name}?`;
    if (!window.confirm(msg)) return;
    const ok = await run(() => evacuationPlanService.deleteZone(zone.id), 'Failed to delete zone');
    if (ok) setZoneModal(null);
  };

  // ── Resident assignment ───────────────────────────────────────────────────
  const openAdd = () => {
    if (!unassigned.length) {
      toast.error('Every resident already has an evacuation zone');
      return;
    }
    setAddResidenceId(String(unassigned[0].residenceId));
    setAddOpen(true);
  };

  const submitAdd = async () => {
    if (!addResidenceId) {
      toast.error('Choose an unassigned resident');
      return;
    }
    const candidate = unassigned.find((u) => String(u.residenceId) === String(addResidenceId));
    const ok = await run(
      () => evacuationPlanService.assignResident(residentsZone.id, {
        residence_id: Number(addResidenceId),
        room: candidate?.room || undefined,
      }),
      'Failed to add resident'
    );
    if (ok) setAddOpen(false);
  };

  const openEditResident = (resident, zoneId) => {
    setEditResident(resident);
    setForm({
      zone_id: zoneId == null ? '' : String(zoneId),
      room: resident.room || '',
      support_level: resident.supportLevel || 'assisted',
      method: resident.method || '',
      plan: resident.plan || '',
    });
  };

  const submitResident = async () => {
    if (!form.room.trim()) { toast.error('Room number is required'); return; }
    if (!form.method.trim()) { toast.error('Evacuation method is required'); return; }
    if (!form.plan.trim()) { toast.error('Plan summary is required'); return; }

    const ok = await run(
      () => evacuationPlanService.updateAssignment(editResident.residenceId, {
        // '' means Unassigned — sent as null so the API keeps the plan and clears the zone.
        zone_id: form.zone_id === '' ? null : Number(form.zone_id),
        room: form.room.trim(),
        support_level: form.support_level,
        method: form.method.trim(),
        plan: form.plan.trim(),
      }),
      'Failed to save resident details'
    );
    if (ok) setEditResident(null);
  };

  const zoneOptions = useMemo(() => ([
    ...zones.map((z) => ({ value: String(z.id), label: z.name })),
    { value: '', label: 'Unassigned' },
  ]), [zones]);

  const columns = useMemo(() => [
    {
      key: 'name',
      header: 'Zone',
      sortable: true,
      render: (zone) => (
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-[38px] h-[38px] rounded-xl grid place-items-center shrink-0 bg-[#EEF3FF] dark:bg-blue-900/20 text-blue-600 dark:text-blue-300">
            <Map className="w-[18px] h-[18px]" strokeWidth={1.9} />
          </span>
          <div className="min-w-0">
            <div className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 leading-tight truncate">
              {zone.name}
            </div>
            <div className="text-[12px] text-[#94A3B8] dark:text-gray-500">
              Created {fmtDate(zone.createdAt)}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'residentCount',
      header: 'Residents',
      sortable: true,
      render: (zone) => (
        <span className="text-[13px] font-semibold text-[#334155] dark:text-gray-300 tabular-nums">
          {zone.residentCount}
        </span>
      ),
    },
    {
      key: 'hasMap',
      header: 'Map',
      sortable: true,
      render: (zone) => (zone.hasMap ? (
        <button
          onClick={(e) => { e.stopPropagation(); setMapZone(zone); }}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 dark:text-emerald-400 bg-[#ECFDF5] dark:bg-emerald-900/10 border border-[#CFF3E4] dark:border-emerald-900/50 rounded-full px-2.5 py-1 max-w-[200px] hover:bg-[#DCFCE7] transition"
          title={zone.mapName}
        >
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{zone.mapName || 'Map uploaded'}</span>
        </button>
      ) : (
        <StatusPill tone="warn" dot={false}>
          <AlertTriangle className="w-3.5 h-3.5" /> Not uploaded
        </StatusPill>
      )),
    },
    {
      key: 'updatedAt',
      header: 'Last updated',
      sortable: true,
      render: (zone) => (
        <span className="text-[13px] text-[#64748B] dark:text-gray-400 whitespace-nowrap">
          {fmtDate(zone.updatedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (zone) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="soft"
            size="sm"
            leftIcon={<Users className="w-4 h-4" />}
            onClick={(e) => { e.stopPropagation(); setResidentsZone(zone); }}
          >
            Residents
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Pencil className="w-4 h-4" />}
            onClick={(e) => { e.stopPropagation(); openZoneEditor(zone); }}
          >
            Manage zone
          </Button>
        </div>
      ),
    },
  ], [unassigned, zones]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Evacuation Plan"
        count={stats.zones}
        subtitle="Create and name evacuation zones, keep floor-plan maps current and make the same information available to staff in the app."
        actions={(
          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => openZoneEditor()}>
            Add zone
          </Button>
        )}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Metric
          label="Zones" value={stats.zones ?? 0} hint="Named evacuation areas" icon={Building2}
          tone="bg-[#EEF3FF] text-blue-600 dark:bg-blue-900/20 dark:text-blue-300"
        />
        <Metric
          label="Residents assigned" value={stats.residentsAssigned ?? 0} hint="Placed in a zone" icon={Users}
          tone="bg-[#F3EEFF] text-violet-600 dark:bg-violet-900/20 dark:text-violet-400"
        />
        <Metric
          label="Maps uploaded" value={stats.mapsUploaded ?? 0} hint="Zones with a floor plan" icon={CheckCircle2}
          tone="bg-[#ECFDF5] text-emerald-600 dark:bg-emerald-900/10 dark:text-emerald-400"
        />
        <Metric
          label="Unassigned residents" value={stats.unassignedResidents ?? 0} hint="Need a zone" icon={AlertTriangle}
          tone="bg-[#FFF1F3] text-rose-600 dark:bg-rose-900/20 dark:text-rose-400"
        />
      </div>

      {/* Unassigned residents — a gap staff can't see, so surface it here. */}
      {!loading && unassigned.length > 0 && (
        <div className="rounded-[20px] border border-[#FBE3C0] dark:border-amber-900/50 bg-[#FFFBEB] dark:bg-amber-900/10 p-5">
          <div className="flex items-start gap-3 flex-wrap">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <h3 className="text-[13.5px] font-bold text-[#92400E] dark:text-amber-300">
                {unassigned.length} resident{unassigned.length === 1 ? '' : 's'} not in an evacuation zone
              </h3>
              <p className="text-[12.5px] text-[#B45309] dark:text-amber-400/80 mt-1">
                Staff only see residents inside a zone. Open a zone&apos;s Residents list to place them.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {unassigned.map((u) => (
                  <button
                    key={u.residenceId}
                    onClick={() => openEditResident(u, null)}
                    className="inline-flex items-center gap-2 bg-white dark:bg-[#262b33] border border-[#FBE3C0] dark:border-amber-900/50 rounded-full pl-1 pr-3 py-1 text-[12.5px] font-semibold text-[#92400E] dark:text-amber-300 hover:border-amber-400 transition"
                  >
                    <Avatar name={u.name} image={u.profilePicUrl ? `${API_ORIGIN}${u.profilePicUrl}` : undefined} size={24} />
                    {u.name}
                    {u.room ? <span className="font-normal opacity-70">Room {u.room}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={zones}
        loading={loading}
        rowKey={(z) => z.id}
        title="Evacuation zones"
        count={zones.length}
        emptyIcon={Map}
        emptyTitle="No zones created yet"
        emptyText="Add a zone to start building your evacuation plan. Zone and map changes appear in the staff app immediately."
        emptyAction={(
          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => openZoneEditor()}>
            Add zone
          </Button>
        )}
      />

      {/* ── Zone editor (create / manage) ─────────────────────────────── */}
      <Modal
        isOpen={!!zoneModal}
        onClose={() => setZoneModal(null)}
        title={zoneModal?.id ? 'Manage zone' : 'Create zone'}
        maxWidth="max-w-lg"
        footer={(
          <div className="flex justify-between items-center gap-2 w-full">
            {zoneModal?.id ? (
              <Button
                variant="danger-soft"
                leftIcon={<Trash2 className="w-4 h-4" />}
                disabled={saving}
                onClick={() => removeZone(zoneModal)}
              >
                Delete zone
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setZoneModal(null)}>Cancel</Button>
              <Button loading={saving} onClick={submitZone}>
                {zoneModal?.id ? 'Save changes' : 'Create zone'}
              </Button>
            </div>
          </div>
        )}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">
              Zone name <span className="text-rose-500">*</span>
            </label>
            <input
              value={zoneName}
              onChange={(e) => setZoneName(e.target.value)}
              maxLength={60}
              placeholder="e.g. Ground Floor Zone"
              className="w-full px-4 py-2.5 border border-[#CBD5E1] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[13.5px]"
            />
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
              Use the same wording shown on your approved floor plan.
            </p>
          </div>

          {zoneModal?.id && (
            <div>
              <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">Current map</label>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-[#FBFCFE] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800">
                <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0 bg-[#EEF3FF] dark:bg-blue-900/20 text-blue-600">
                  <Map className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">
                    {zoneModal.hasMap ? zoneModal.mapName : 'No map uploaded'}
                  </div>
                  <div className="text-[12px] text-[#94A3B8] dark:text-gray-500">
                    {zoneModal.hasMap ? `Updated ${fmtDate(zoneModal.updatedAt)}` : 'Upload a floor-plan map below.'}
                  </div>
                </div>
                {zoneModal.hasMap && (
                  <Button variant="secondary" size="sm" onClick={() => setMapZone(zoneModal)}>View map</Button>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">
              {zoneModal?.hasMap ? 'Replace floor-plan map' : 'Floor-plan map'}
            </label>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-[#CBD5E1] dark:border-gray-700 cursor-pointer hover:border-blue-400 transition">
              <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={pickMap} className="hidden" />
              <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0 bg-[#EEF3FF] dark:bg-blue-900/20 text-blue-600">
                <Upload className="w-4 h-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">
                  {zoneMapFile ? zoneMapFile.name : (zoneModal?.hasMap ? 'Choose replacement map' : 'Upload a map')}
                </span>
                <span className="block text-[12px] text-[#94A3B8] dark:text-gray-500">
                  {zoneModal?.hasMap
                    ? 'Optional · leave empty to keep the current map'
                    : 'Optional · PNG, JPG, WebP or PDF up to 2 MB'}
                </span>
              </span>
            </label>
          </div>
        </div>
      </Modal>

      {/* ── Zone residents ───────────────────────────────────────────────── */}
      <Modal
        isOpen={!!residentsZone}
        onClose={() => setResidentsZone(null)}
        title={residentsZone ? residentsZone.name : ''}
        maxWidth="max-w-2xl"
        closeOnBackdrop
      >
        {residentsZone && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[13px] text-[#64748B] dark:text-gray-400">
                {residentsZone.residentCount} resident{residentsZone.residentCount === 1 ? '' : 's'} assigned ·
                {' '}review evacuation support details for this zone.
              </p>
              <Button
                size="sm"
                leftIcon={<UserPlus className="w-4 h-4" />}
                disabled={saving || unassigned.length === 0}
                onClick={openAdd}
              >
                Add resident
              </Button>
            </div>

            {residentsZone.residents.length === 0 ? (
              <div className="p-8 text-center rounded-2xl bg-[#FBFCFE] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800">
                <Users className="w-9 h-9 mx-auto text-[#CBD5E1] dark:text-gray-600" />
                <p className="text-[13px] text-[#94A3B8] dark:text-gray-500 mt-2">
                  No residents are assigned to this zone.
                </p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {residentsZone.residents.map((r) => (
                  <div
                    key={r.residenceId}
                    className="flex items-center gap-3 p-3 rounded-2xl bg-[#FBFCFE] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800"
                  >
                    <Avatar name={r.name} image={r.profilePicUrl ? `${API_ORIGIN}${r.profilePicUrl}` : undefined} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{r.name}</div>
                      <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 flex items-center gap-1.5 flex-wrap">
                        Room {r.room || '—'}
                        <span aria-hidden="true">·</span>
                        <SupportDot level={r.supportLevel} />
                        {r.supportLabel}
                        {r.method ? <><span aria-hidden="true">·</span>{r.method}</> : null}
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Pencil className="w-4 h-4" />}
                      onClick={() => openEditResident(r, residentsZone.id)}
                    >
                      Edit
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Add resident to zone ─────────────────────────────────────────── */}
      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add resident to zone"
        maxWidth="max-w-md"
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button leftIcon={<Plus className="w-4 h-4" />} loading={saving} onClick={submitAdd}>
              Add resident
            </Button>
          </div>
        )}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">
              Resident <span className="text-rose-500">*</span>
            </label>
            <Select
              value={addResidenceId}
              onChange={setAddResidenceId}
              options={unassigned.map((u) => ({
                value: String(u.residenceId),
                label: u.room ? `${u.name} · Room ${u.room}` : u.name,
              }))}
            />
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
              Only residents without an evacuation zone are listed.
            </p>
          </div>
          {(() => {
            const candidate = unassigned.find((u) => String(u.residenceId) === String(addResidenceId));
            if (!candidate) return null;
            return (
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#FBFCFE] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800">
                <Avatar name={candidate.name} image={candidate.profilePicUrl ? `${API_ORIGIN}${candidate.profilePicUrl}` : undefined} />
                <div className="min-w-0">
                  <div className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{candidate.name}</div>
                  <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 flex items-center gap-1.5">
                    Room {candidate.room || '—'} <span aria-hidden="true">·</span>
                    <SupportDot level={candidate.supportLevel} /> {candidate.supportLabel}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </Modal>

      {/* ── Edit resident evacuation details ─────────────────────────────── */}
      <Modal
        isOpen={!!editResident}
        onClose={() => setEditResident(null)}
        title="Edit evacuation details"
        maxWidth="max-w-2xl"
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditResident(null)}>Cancel</Button>
            <Button loading={saving} onClick={submitResident}>Save changes</Button>
          </div>
        )}
      >
        {editResident && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#FBFCFE] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800">
              <Avatar name={editResident.name} image={editResident.profilePicUrl ? `${API_ORIGIN}${editResident.profilePicUrl}` : undefined} />
              <div className="min-w-0">
                <div className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{editResident.name}</div>
                <div className="text-[12px] text-[#94A3B8] dark:text-gray-500">
                  Resident identity is managed from the Residents profile.
                </div>
              </div>
            </div>

            <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
              Updates appear in the staff app immediately.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">
                  Assigned zone <span className="text-rose-500">*</span>
                </label>
                <Select
                  value={form.zone_id}
                  onChange={(v) => setForm((f) => ({ ...f, zone_id: v }))}
                  options={zoneOptions}
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">
                  Room number <span className="text-rose-500">*</span>
                </label>
                <input
                  value={form.room}
                  onChange={(e) => setForm((f) => ({ ...f, room: e.target.value }))}
                  maxLength={20}
                  className="w-full px-4 py-2.5 border border-[#CBD5E1] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[13.5px]"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">
                  Support level <span className="text-rose-500">*</span>
                </label>
                <Select
                  value={form.support_level}
                  onChange={(v) => setForm((f) => ({ ...f, support_level: v }))}
                  options={SUPPORT_OPTIONS}
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">
                  Evacuation method <span className="text-rose-500">*</span>
                </label>
                <input
                  value={form.method}
                  onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}
                  maxLength={90}
                  placeholder="e.g. Evacuation equipment required"
                  className="w-full px-4 py-2.5 border border-[#CBD5E1] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[13.5px]"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-1.5">
                  Plan summary <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={form.plan}
                  onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-[#CBD5E1] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[13.5px]"
                />
                <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
                  Keep this concise and aligned with the approved personal emergency evacuation plan.
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Map viewer ───────────────────────────────────────────────────── */}
      <Modal
        isOpen={!!mapZone}
        onClose={() => setMapZone(null)}
        title={mapZone ? `${mapZone.name} — ${mapZone.mapName}` : ''}
        maxWidth="max-w-4xl"
        closeOnBackdrop
      >
        {mapZone && (
          <div className="space-y-3">
            {mapZone.mapType?.startsWith('image/') ? (
              <img
                src={`${API_ORIGIN}${mapZone.mapUrl}`}
                alt={`${mapZone.name} floor-plan map`}
                className="w-full rounded-2xl border border-[#EEF2F8] dark:border-gray-800"
              />
            ) : (
              <div className="p-8 rounded-2xl bg-[#FBFCFE] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800 text-center">
                <FileText className="w-10 h-10 mx-auto text-[#94A3B8]" />
                <p className="text-[13.5px] font-semibold text-[#1E293B] dark:text-gray-200 mt-3">{mapZone.mapName}</p>
                <a
                  href={`${API_ORIGIN}${mapZone.mapUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block mt-3 text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Open PDF map
                </a>
              </div>
            )}
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500">
              Follow the approved evacuation plan and local management instructions. Map changes are controlled by management.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EvacuationPlan;
